import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Home, Print } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';
import { fmt as fmtAmt, sym, locale } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => new Date(d).toLocaleDateString(locale(), { year: 'numeric', month: 'long', day: 'numeric' });

const ReceiptsList = () => {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [error,    setError]    = useState('');
  const [printDoc, setPrintDoc] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/finance/payments`, { headers: hdrs() })
      .then(r => setReceipts(r.data?.data || []))
      .catch(err => setError(err.response?.data?.error || 'Failed to load receipts'));
  }, []);

  useEffect(() => {
    const clear = () => setPrintDoc(null);
    window.addEventListener('afterprint', clear);
    return () => window.removeEventListener('afterprint', clear);
  }, []);

  const handlePrint = (rec) => {
    setPrintDoc(rec);
    setTimeout(() => window.print(), 60);
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #rec-print-area, #rec-print-area * { visibility: visible; }
          #rec-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 40px; box-sizing: border-box; font-family: serif; }
        }
        @media screen { #rec-print-area { display: none; } }
      `}</style>

      {printDoc && (
        <Box id="rec-print-area">
          <CompanyHeader title="Payment Receipt" subtitle={printDoc.receiptNo} />
          <Box textAlign="center" mb={2}>
            <Typography variant="body2">Date: {fmtDate(printDoc.paymentDate)}</Typography>
          </Box>
          <Box sx={{ border: '1px solid #ccc', p: 2, mb: 2 }}>
            <Typography variant="body2"><strong>Received From:</strong> {printDoc.learnerAdmNo}</Typography>
            <Typography variant="body2"><strong>For Invoice:</strong> {printDoc.invoiceNo}</Typography>
          </Box>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Reference</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>Amount ({sym()})</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{printDoc.transactionDescription}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{printDoc.reference}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{fmtAmt(printDoc.amount)}</td>
              </tr>
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                <td colSpan={2} style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>TOTAL RECEIVED</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{sym()} {fmtAmt(printDoc.amount)}</td>
              </tr>
            </tbody>
          </table>
          <Typography variant="caption">Bank Account: {printDoc.bankAccountNo}</Typography><br />
          <Typography variant="caption">GL Account: {printDoc.glAccount || '—'}</Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Received by: _______________________</Typography>
            <Typography variant="caption">Authorized by: _______________________</Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ maxWidth: 950, mx: 'auto', mt: 4, mb: 4 }}>
        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h5" color="primary">Payment Receipts</Typography>
              <Tooltip title="Back to Dashboard">
                <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {receipts.length === 0 ? (
              <Typography color="text.secondary">No receipts found.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Receipt No</strong></TableCell>
                    <TableCell><strong>Learner</strong></TableCell>
                    <TableCell><strong>Invoice No</strong></TableCell>
                    <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                    <TableCell><strong>Bank</strong></TableCell>
                    <TableCell><strong>Reference</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="center"><strong>Print</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map(rec => (
                    <TableRow key={rec.receiptNo} hover>
                      <TableCell><strong>{rec.receiptNo}</strong></TableCell>
                      <TableCell>{rec.learnerAdmNo}</TableCell>
                      <TableCell>{rec.invoiceNo}</TableCell>
                      <TableCell align="right">{fmtAmt(rec.amount)}</TableCell>
                      <TableCell>{rec.bankAccountNo}</TableCell>
                      <TableCell>{rec.reference}</TableCell>
                      <TableCell>{new Date(rec.paymentDate).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Print Receipt">
                          <IconButton size="small" onClick={() => handlePrint(rec)}>
                            <Print fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default ReceiptsList;
